/**
 * La marca de TurnPro.
 *
 * Un unico dibujo para las cuatro aplicaciones. Antes el simbolo vivia en
 * `apps/app/src/components/marca.tsx` y el nombre se escribia suelto en la
 * landing, en la documentacion y en admin: cuatro lugares que podian
 * desincronizarse sin que nada fallara, que es exactamente lo que le paso a la
 * paleta (`docs/DECISIONES.md`, D-050).
 *
 * **El simbolo es la `t` de TurnPro con un turno tomado sobre la barra.** La
 * inicial se lee como letra y el bloque de la derecha como lo que el producto
 * hace: reservar un pedazo de tiempo.
 *
 * **Es el mismo en tema claro y en oscuro, a proposito.** El simbolo trae su
 * propio fondo —el cuadrado tomate— asi que no cambia con el tema: la marca se
 * reconoce igual en las dos, y no hay una version que alguien tenga que
 * acordarse de mantener. Lo unico que sigue al tema es el nombre al lado, que
 * es texto y toma el color del contexto con `currentColor`.
 */

import type { ReactNode } from "react";

import { cn } from "./cn";

const CUADRO = {
  sm: "size-7",
  md: "size-9",
  lg: "size-12",
} as const;

const NOMBRE = {
  sm: "text-base",
  md: "text-lg",
  lg: "text-2xl",
} as const;

export type TamanoMarca = keyof typeof CUADRO;

/**
 * Solo el simbolo, para los espacios chicos.
 *
 * El glifo ocupa la mitad del cuadrado. Es menos de lo que uno dibujaria a
 * ojo, y es lo que lo hace legible: con el aire justo, el bloque y la barra se
 * siguen distinguiendo a 16 px, que es el tamaño del favicon.
 */
export function Isotipo({
  size = "md",
  className,
}: {
  size?: TamanoMarca;
  className?: string;
}): ReactNode {
  return (
    <span
      className={cn(
        "inline-flex shrink-0 items-center justify-center rounded-[23%] bg-brand-600 text-white",
        CUADRO[size],
        className,
      )}
      aria-hidden="true"
    >
      <svg viewBox="0 0 24 24" className="size-1/2" fill="none">
        {/* El asta de la t, con el pie que se curva a la derecha. */}
        <path
          d="M8.9 3.3V15.8a3.9 3.9 0 0 0 3.9 3.9h2"
          stroke="currentColor"
          strokeWidth="2.9"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        {/* La barra. */}
        <path d="M4.7 8.7h6.3" stroke="currentColor" strokeWidth="2.9" strokeLinecap="round" />
        {/* El turno tomado sobre la barra. */}
        <rect x="13.2" y="6.6" width="8.9" height="4.2" rx="1.5" fill="currentColor" />
      </svg>
    </span>
  );
}

/**
 * El simbolo con el nombre, que es la forma completa de la marca.
 *
 * No lleva enlace: cada aplicacion lo envuelve con el suyo —`<Link>` donde hay
 * router, `<a>` en la landing— porque el destino tambien cambia. Meter aqui un
 * `<Link>` de react-router obligaria a la landing, que no tiene router, a
 * instalarlo para dibujar su logo.
 *
 * El nombre usa la cara propia del producto (`--font-display`, de
 * `@turnpro/tema`) escrita como variable y no con la clase `titulo`: esa clase
 * la define cada app en su `index.css`, y un componente compartido que dependa
 * de una clase del consumidor se dibuja mal en silencio el dia que una app no
 * la tenga.
 */
export function Marca({
  size = "md",
  className,
}: {
  size?: TamanoMarca;
  className?: string;
}): ReactNode {
  return (
    <span className={cn("inline-flex items-center gap-2.5", className)}>
      <Isotipo size={size} />
      <span
        className={cn("font-semibold tracking-tight", NOMBRE[size])}
        style={{ fontFamily: "var(--font-display)" }}
      >
        TurnPro
      </span>
    </span>
  );
}
